import React from "react";
import { FiSliders } from "react-icons/fi";
import type { ImageHeightOverride, ImageHeightValue, ImageOptions } from "@bcc/core-renderers";
import { Modal } from "./Modal";

type ImageOptionsModalProps = {
	show: boolean;
	value: ImageOptions;
	onChange: (next: ImageOptions) => void;
	onCancel: () => void;
	onSave: () => void;
	onReset: () => void;
	saveDisabled?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeValue = (value?: number, fallback = 0) =>
	typeof value === "number" && Number.isFinite(value) ? value : fallback;

const buildHeightValue = (value?: ImageHeightValue): ImageHeightValue => ({
	enabled: value?.enabled ?? false,
	value: normalizeValue(value?.value)
});

const buildOverride = (override?: ImageHeightOverride): ImageHeightOverride => ({
	enabled: override?.enabled ?? false,
	minHeight: buildHeightValue(override?.minHeight),
	maxHeight: buildHeightValue(override?.maxHeight),
	height: buildHeightValue(override?.height)
});

type FitOption = "cover" | "contain" | "fill" | "none";
const IMAGE_FIT_OPTIONS: { value: "" | FitOption; label: string }[] = [
	{ value: "", label: "Default" },
	{ value: "cover", label: "Cover" },
	{ value: "contain", label: "Contain" },
	{ value: "fill", label: "Fill" },
	{ value: "none", label: "None" }
];

export const createDefaultImageOptions = (): ImageOptions => ({
	center: { enabled: false, x: 0, y: 0 },
	overrides: {
		mobile: buildOverride(),
		tablet: buildOverride(),
		desktop: buildOverride()
	}
});

export const normalizeImageOptions = (value?: ImageOptions | null): ImageOptions => ({
	center: {
		enabled: value?.center?.enabled ?? false,
		x: clamp(normalizeValue(value?.center?.x), -1, 1),
		y: clamp(normalizeValue(value?.center?.y), -1, 1)
	},
	overrides: {
		mobile: buildOverride(value?.overrides?.mobile),
		tablet: buildOverride(value?.overrides?.tablet),
		desktop: buildOverride(value?.overrides?.desktop)
	},
	...(value?.fit && { fit: value.fit })
});

const ToggleField: React.FC<{
	label: string;
	value: boolean;
	onChange: (next: boolean) => void;
	disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
	<label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
		<input
			type="checkbox"
			className="h-4 w-4 rounded border-slate-300 text-slate-900"
			checked={value}
			disabled={disabled}
			onChange={(event) => onChange(event.target.checked)}
		/>
		{label}
	</label>
);

const HeightField: React.FC<{
	label: string;
	value: ImageHeightValue;
	onChange: (next: ImageHeightValue) => void;
	disabled: boolean;
}> = ({ label, value, onChange, disabled }) => (
	<div className="space-y-1">
		<ToggleField
			label={label}
			value={value.enabled ?? false}
			onChange={(enabled) => onChange({ ...value, enabled })}
			disabled={disabled}
		/>
		<input
			type="number"
			min={0}
			step={1}
			value={value.value ?? 0}
			disabled={disabled || !value.enabled}
			onChange={(event) => {
				const raw = event.target.value;
				const parsed = raw === "" ? 0 : Number(raw);
				onChange({ ...value, value: Number.isNaN(parsed) ? 0 : parsed });
			}}
			className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
		/>
	</div>
);

const DeviceOverride: React.FC<{
	label: string;
	value: ImageHeightOverride;
	onChange: (next: ImageHeightOverride) => void;
}> = ({ label, value, onChange }) => {
	const update = (patch: Partial<ImageHeightOverride>) => onChange({ ...value, ...patch });
	const disabled = !value.enabled;
	return (
		<div className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
			<div className="flex items-center justify-between">
				<ToggleField label={`Override ${label}`} value={value.enabled ?? false} onChange={(enabled) => update({ enabled })} />
				<span className="text-xs text-slate-400">px</span>
			</div>
			<div className="grid gap-3 sm:grid-cols-3">
				<HeightField
					label="Min height"
					value={value.minHeight ?? buildHeightValue()}
					onChange={(minHeight) => update({ minHeight })}
					disabled={disabled}
				/>
				<HeightField
					label="Max height"
					value={value.maxHeight ?? buildHeightValue()}
					onChange={(maxHeight) => update({ maxHeight })}
					disabled={disabled}
				/>
				<HeightField
					label="Height"
					value={value.height ?? buildHeightValue()}
					onChange={(height) => update({ height })}
					disabled={disabled}
				/>
			</div>
		</div>
	);
};

/** Advanced image options modal (center, fit, responsive heights). */
export const ImageOptionsModal: React.FC<ImageOptionsModalProps> = ({
	show,
	value,
	onChange,
	onCancel,
	onSave,
	onReset,
	saveDisabled
}) => {
	const center = value.center ?? { enabled: false, x: 0, y: 0 };
	const overrides = value.overrides ?? {};
	return (
		<Modal show={show} onHide={onCancel} size="md" centered>
			<Modal.Header className="bg-slate-900 text-white" closeButton>
				<div className="flex items-center gap-2">
					<FiSliders />
					<Modal.Title>Custom image options</Modal.Title>
				</div>
			</Modal.Header>
			<Modal.Body className="bg-slate-50">
				<div className="space-y-5">
					<div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
						<label className="block text-xs font-semibold text-slate-600">Image fit (object-fit)</label>
						<select
							value={value.fit ?? ""}
							onChange={(e) => {
								const v = e.target.value as "" | FitOption;
								onChange({ ...value, fit: (v || undefined) as ImageOptions["fit"] });
							}}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						>
							{IMAGE_FIT_OPTIONS.map((o) => (
								<option key={o.value || "default"} value={o.value}>
									{o.label}
								</option>
							))}
						</select>
						<p className="text-[11px] text-slate-500">e.g. Cover to fill the area and crop; Contain to fit inside.</p>
					</div>
					<div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
						<ToggleField
							label="Enable custom center"
							value={center.enabled ?? false}
							onChange={(enabled) => onChange({ ...value, center: { ...center, enabled } })}
						/>
						<div className="grid gap-3 sm:grid-cols-2">
							<div className="space-y-1">
								<label className="text-xs font-semibold text-slate-600">Center X (-1 to 1)</label>
								<input
									type="number"
									min={-1}
									max={1}
									step={0.05}
									value={center.x ?? 0}
									disabled={!center.enabled}
									onChange={(event) => {
										const raw = Number(event.target.value);
										const next = Number.isNaN(raw) ? 0 : clamp(raw, -1, 1);
										onChange({ ...value, center: { ...center, x: next } });
									}}
									className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								/>
							</div>
							<div className="space-y-1">
								<label className="text-xs font-semibold text-slate-600">Center Y (-1 to 1)</label>
								<input
									type="number"
									min={-1}
									max={1}
									step={0.05}
									value={center.y ?? 0}
									disabled={!center.enabled}
									onChange={(event) => {
										const raw = Number(event.target.value);
										const next = Number.isNaN(raw) ? 0 : clamp(raw, -1, 1);
										onChange({ ...value, center: { ...center, y: next } });
									}}
									className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
								/>
							</div>
						</div>
						<p className="text-[11px] text-slate-500">
							Use -1 for top/left, 0 for center, 1 for bottom/right.
						</p>
					</div>

					<div className="space-y-3">
						<div>
							<p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Height overrides</p>
							<p className="text-xs text-slate-500">Enable a device range first, then enable each value. Desktop applies to all viewports unless tablet or mobile overrides are set.</p>
						</div>
						<DeviceOverride
							label="mobile"
							value={buildOverride(overrides.mobile)}
							onChange={(next) => onChange({ ...value, overrides: { ...overrides, mobile: next } })}
						/>
						<DeviceOverride
							label="tablet"
							value={buildOverride(overrides.tablet)}
							onChange={(next) => onChange({ ...value, overrides: { ...overrides, tablet: next } })}
						/>
						<DeviceOverride
							label="desktop"
							value={buildOverride(overrides.desktop)}
							onChange={(next) => onChange({ ...value, overrides: { ...overrides, desktop: next } })}
						/>
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer className="bg-white">
				<button
					type="button"
					onClick={onReset}
					className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
				>
					Reset
				</button>
				<div className="flex-1" />
				<button
					type="button"
					onClick={onCancel}
					className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={onSave}
					disabled={saveDisabled}
					className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
				>
					Save options
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default ImageOptionsModal;
